/**
 * Browser storage that holds the signed-in person's own data (#119 fix batch
 * B: the applicant form's stash of unsaved answers). Every such localStorage
 * key starts with USER_SCOPED_PREFIX, so the session can sweep them without
 * knowing which feature wrote them:
 *
 * - **Sign-out** clears them all (`clearUserScopedStorage`): the next person
 *   at this browser must not find the last one's answers.
 * - **Sign-out stops the writers first** (`signOutUserScopedStorage`, RW3):
 *   it marks "signing out" (in memory and in storage, so other tabs see it),
 *   tells the `onSignOut` listeners (the answer queues discard what they hold),
 *   then sweeps. The navigation sign-out starts fires `pagehide` and
 *   `visibilitychange`; with the mark set, nothing writes the data back. The
 *   mark lasts until the next sign-in (`claimUserScopedStorage`), or at most
 *   SIGN_OUT_MARK_MAX_AGE_MS (NI5): it holds the time it was set.
 * - **Sign-out sends first** (NW1, user decision 2026-10-23): before any of
 *   that, `sendBeforeSignOut` asks every `beforeSignOut` sender (the answer
 *   queues) to send what the server does not hold yet, while the session is
 *   still valid, for at most SIGN_OUT_FLUSH_MS. When one cannot, the caller
 *   asks the person whether to sign out anyway.
 * - **A different person signing in** clears them too (`claimUserScopedStorage`
 *   compares the account recorded with the data). The same person signing in
 *   again keeps them: a session that expired mid-form is exactly what the
 *   stash is there to rescue.
 *
 * Every access is wrapped: a browser that refuses storage has nothing to sweep.
 */

export const USER_SCOPED_PREFIX = 'user-data:';
/** The account the user-scoped data belongs to. */
export const USER_SCOPED_OWNER_KEY = 'user-data-owner';
/** Present from sign-out until the next sign-in: no user-scoped data may be written. Outside the prefix, so the sweep keeps it. */
export const USER_SCOPED_SIGNING_OUT_KEY = 'user-data-signing-out';

/** A sign-out mark older than this is ignored (NI5): a sign-out takes seconds, not a day. */
export const SIGN_OUT_MARK_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** How long sign-out waits for unsent answers to reach the server (NW1). */
export const SIGN_OUT_FLUSH_MS = 3_000;

/** This page started signing out (also covers a storage that refuses the mark). */
let signingOut = false;
const signOutListeners = new Set<() => void>();
/** Senders asked before sign-out: true once the server holds everything they had. */
const signOutSenders = new Set<() => Promise<boolean>>();

export type UserScopedStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export function browserLocalStorage(): UserScopedStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Remove every user-scoped entry and the owner record. */
export function clearUserScopedStorage(storage: UserScopedStorage | null = browserLocalStorage()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(USER_SCOPED_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
    storage.removeItem(USER_SCOPED_OWNER_KEY);
  } catch {
    // Storage refused: nothing readable was left behind either.
  }
}

/** Told when sign-out starts, before the sweep: stop writing user-scoped data. */
export function onSignOut(listener: () => void): () => void {
  signOutListeners.add(listener);
  return () => {
    signOutListeners.delete(listener);
  };
}

/** Asked before sign-out to send what it holds (NW1); resolves true when the server holds it all. */
export function beforeSignOut(sender: () => Promise<boolean>): () => void {
  signOutSenders.add(sender);
  return () => {
    signOutSenders.delete(sender);
  };
}

/**
 * NW1: every `beforeSignOut` sender, in parallel, for at most `timeoutMs`.
 * True only when all of them sent everything in time; a sender that fails,
 * throws or is still busy at the limit makes it false (it keeps working).
 */
export async function sendBeforeSignOut(timeoutMs: number = SIGN_OUT_FLUSH_MS): Promise<boolean> {
  const senders = [...signOutSenders];
  if (senders.length === 0) return true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  const all = Promise.all(senders.map((send) => send().catch(() => false))).then((sent) => sent.every(Boolean));
  try {
    return await Promise.race([all, late]);
  } finally {
    clearTimeout(timer);
  }
}

/** Whether sign-out has started, in this page or another tab of this browser. Writers of user-scoped data check it. */
export function isSigningOut(storage: UserScopedStorage | null = browserLocalStorage()): boolean {
  if (signingOut) return true;
  try {
    const mark = storage?.getItem(USER_SCOPED_SIGNING_OUT_KEY);
    if (mark == null) return false;
    // NI5: a stale mark (a sign-out that never reached a sign-in) stops nothing.
    // A mark without a readable time still counts: fail closed.
    const at = Number(mark);
    if (Number.isFinite(at) && Date.now() - at > SIGN_OUT_MARK_MAX_AGE_MS) {
      storage?.removeItem(USER_SCOPED_SIGNING_OUT_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** The account the user-scoped data belongs to now (null: nobody on record, or storage refused). */
export function userScopedOwner(storage: UserScopedStorage | null = browserLocalStorage()): string | null {
  try {
    return storage?.getItem(USER_SCOPED_OWNER_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * Sign-out (RW3): mark it, stop every writer (`onSignOut` listeners), then
 * sweep. In that order, so a write the sign-out navigation triggers cannot
 * put the person's data back after the sweep.
 */
export function signOutUserScopedStorage(storage: UserScopedStorage | null = browserLocalStorage()): void {
  signingOut = true;
  try {
    storage?.setItem(USER_SCOPED_SIGNING_OUT_KEY, String(Date.now()));
  } catch {
    // The in-memory mark still covers this page.
  }
  for (const listener of [...signOutListeners]) {
    try {
      listener();
    } catch {
      // One writer failing to stop must not keep the sweep from running.
    }
  }
  clearUserScopedStorage(storage);
}

/** Lift the sign-out mark: someone signed in (and tests). */
export function endSignOut(storage: UserScopedStorage | null = browserLocalStorage()): void {
  signingOut = false;
  try {
    storage?.removeItem(USER_SCOPED_SIGNING_OUT_KEY);
  } catch {
    // Storage refused: nothing to lift there.
  }
}

/**
 * A person is signed in: if the user-scoped data belongs to someone else (or
 * to nobody on record), sweep it, then record this account as its owner.
 * Lifts the sign-out mark.
 */
export function claimUserScopedStorage(accountId: string, storage: UserScopedStorage | null = browserLocalStorage()): void {
  endSignOut(storage);
  if (!storage) return;
  try {
    if (storage.getItem(USER_SCOPED_OWNER_KEY) === accountId) return;
    clearUserScopedStorage(storage);
    storage.setItem(USER_SCOPED_OWNER_KEY, accountId);
  } catch {
    // Storage refused: the stash cannot be written either.
  }
}
